import React, { useRef } from 'react';
import { Formik, Field, ErrorMessage, FormikProps } from 'formik';
import InputBlock from '$/components/global/inputBlock';
import MonthDatePickerField from '$/components/datePickerField/MonthDatePickerField';
import dayjs from 'dayjs';
import BixButton from '$/components/global/bixButton';
import * as Bixby from '$/core';
import * as Yup from 'yup';
import {
  FINANCIALS_CATEGORY,
  LENDER_PRESENTATIONS_CATEGORY,
  ORG_CHART_CATEGORY,
} from '$/components/company-documents-upload/categories';
import './index.scss';

export interface CategoryFormValues {
  clientCompanyId?: string;
  companyDocumentCategory_Id?: string;
  financialDocumentType?: Bixby.Enums.FinancialDocumentType;
  financialDocumentDate?: Date;
  orgChartPageNumber?: number;
}

interface IProps {
  id: string;
  allCategories: Bixby.Models.CompanyDocumentCategory[];
  currentCompanyDocumentCategories: Bixby.Models.CompanyDocumentCompanyDocumentCategory[] | undefined;
  onSave: (id: string, categories: Bixby.Models.CompanyDocumentCompanyDocumentCategory[]) => void;
  onCancel: (id: string) => void;
  fiscalYearEnd?: Date | null;
}

const CompanyDocumentsCategoryPicker: React.FC<IProps> = ({
  id,
  allCategories,
  currentCompanyDocumentCategories,
  onSave,
  onCancel,
  fiscalYearEnd,
}) => {
  const formikRef = useRef<FormikProps<CategoryFormValues>>(null);

  const initialValues: CategoryFormValues = {
    companyDocumentCategory_Id: '',
    financialDocumentType: undefined,
    financialDocumentDate: undefined,
    orgChartPageNumber: undefined,
  };

  const validationSchema = Yup.object({
    companyDocumentCategory_Id: Yup.string().required('You must select a category.'),
    financialDocumentType: Yup.string().when('companyDocumentCategory_Id', (companyDocumentCategory_Id, schema) => {
      if (companyDocumentCategory_Id.toString() === FINANCIALS_CATEGORY)
        return schema.required('You must specify the time period of the financial document.');
      else if (companyDocumentCategory_Id.toString() === LENDER_PRESENTATIONS_CATEGORY)
        return schema.required('You must specify the time period of the lender document.');
      return schema;
    }),
    financialDocumentDate: Yup.date().when('companyDocumentCategory_Id', (companyDocumentCategory_Id, schema) => {
      if (companyDocumentCategory_Id.toString() === FINANCIALS_CATEGORY)
        return schema.required('You must specify the time period of the financial document.');
      else if (companyDocumentCategory_Id.toString() === LENDER_PRESENTATIONS_CATEGORY)
        return schema.required('You must specify the time period of the lender document.');
      return schema;
    }),
    orgChartPageNumber: Yup.string().when('companyDocumentCategory_Id', (companyDocumentCategory_Id, schema) => {
      if (companyDocumentCategory_Id.toString() === ORG_CHART_CATEGORY)
        return schema.required('You must specifiy the page number of the org chart in the document.');
      return schema;
    }),
  });

  const handleSaveCategory = () => {
    if (!formikRef.current) return;

    const { values } = formikRef.current;
    const categoryReference = allCategories.find(_ => _.id === values.companyDocumentCategory_Id);
    if (!categoryReference || !categoryReference?.id || !categoryReference?.name) return;

    const category: Bixby.Models.CompanyDocumentCompanyDocumentCategory = {
      companyDocumentCategory_Id: categoryReference.id,
      categoryName: categoryReference.name,
      financialDocumentType: values.financialDocumentType,
      financialDocumentDate: values.financialDocumentDate,
      orgChartPageNumber: values.orgChartPageNumber,
    };
    //if fye tagged auto tag Q4 as well, viseversa
    let associatedCategory: Bixby.Models.CompanyDocumentCompanyDocumentCategory | null = null;
    if (category.financialDocumentType && category.financialDocumentType === Bixby.Enums.FinancialDocumentType.FYE) {
      associatedCategory = {
        companyDocumentCategory_Id: category.companyDocumentCategory_Id,
        categoryName: category.categoryName,
        financialDocumentType: Bixby.Enums.FinancialDocumentType.Q,
        financialDocumentDate: category.financialDocumentDate,
      };
    } else if (
      category.financialDocumentType &&
      category.financialDocumentType === Bixby.Enums.FinancialDocumentType.Q
    ) {
      const month = category.financialDocumentDate !== undefined ? category.financialDocumentDate.getMonth() : 0;
      const fyeMonth = dayjs(fiscalYearEnd).month();
      if (month === fyeMonth) {
        associatedCategory = {
          companyDocumentCategory_Id: category.companyDocumentCategory_Id,
          categoryName: category.categoryName,
          financialDocumentType: Bixby.Enums.FinancialDocumentType.FYE,
          financialDocumentDate: category.financialDocumentDate,
        };
      }
    }

    let allCurrentCategories: Bixby.Models.CompanyDocumentCompanyDocumentCategory[] =
      currentCompanyDocumentCategories || [];

    if (!Bixby.Utils.arrayIsPopulated(allCurrentCategories)) {
      allCurrentCategories = [category];
      if (associatedCategory !== null) {
        allCurrentCategories = allCurrentCategories.concat(associatedCategory);
      }
    } else {
      const matchingCategories = allCurrentCategories.filter(
        _ => _.companyDocumentCategory_Id === categoryReference.id
      );
      if (!Bixby.Utils.arrayIsPopulated(matchingCategories)) {
        allCurrentCategories = allCurrentCategories.concat(category);
        if (associatedCategory) {
          allCurrentCategories = allCurrentCategories.concat(associatedCategory);
        }
      } else if (categoriesWithExtras.includes(categoryReference.id)) {
        const matchingExtras = matchingCategories.filter(
          _ =>
            _.financialDocumentType === category.financialDocumentType &&
            dayjs(_.financialDocumentDate).isSame(category.financialDocumentDate, 'month') // 'month' parameter checks month and year
        );
        if (!Bixby.Utils.arrayIsPopulated(matchingExtras)) {
          allCurrentCategories = allCurrentCategories.concat(category);
        }
        if (associatedCategory !== null) {
          const matchingExtras = matchingCategories.filter(
            _ =>
              _.financialDocumentType === associatedCategory?.financialDocumentType &&
              dayjs(_.financialDocumentDate).isSame(associatedCategory?.financialDocumentDate, 'month') // 'month' parameter checks month and year
          );
          if (!Bixby.Utils.arrayIsPopulated(matchingExtras)) {
            allCurrentCategories = allCurrentCategories.concat(associatedCategory);
          }
        }
      }
    }

    onSave(id, allCurrentCategories);
  };

  const categoriesWithExtras = [FINANCIALS_CATEGORY, LENDER_PRESENTATIONS_CATEGORY];

  return (
    <div className="category_picker">
      <Formik
        innerRef={formikRef}
        initialValues={initialValues as CategoryFormValues}
        validationSchema={validationSchema}
        onSubmit={handleSaveCategory}
        render={({ values, setFieldValue, setFieldTouched, resetForm, errors }) => (
          <>
            <>
              <InputBlock title="Document Category">
                <Field
                  name="companyDocumentCategory_Id"
                  render={() => {
                    return (
                      <>
                        <select
                          value={values.companyDocumentCategory_Id || ''}
                          onBlur={() => setFieldTouched('companyDocumentCategory_Id')}
                          onChange={e => {
                            setFieldValue('companyDocumentCategory_Id', e.currentTarget.value);
                            resetForm({ values: { companyDocumentCategory_Id: e.currentTarget.value } });
                          }}>
                          <option disabled key="-1" value="">
                            Select a category
                          </option>
                          {allCategories.map((category, ix) => {
                            return (
                              <option key={ix} value={category.id}>
                                {category.name}
                              </option>
                            );
                          })}
                        </select>
                      </>
                    );
                  }}
                />
              </InputBlock>
              <ErrorMessage name="companyDocumentCategory_Id">
                {msg => <div className="error error-message">{msg}</div>}
              </ErrorMessage>
            </>
            {categoriesWithExtras.includes(values.companyDocumentCategory_Id || '') && (
              <>
                <>
                  <InputBlock title="Type">
                    <Field
                      name="companyDocumentCategory_Id"
                      render={() => {
                        return (
                          <>
                            <select
                              value={values.financialDocumentType || ''}
                              required
                              onBlur={() => setFieldTouched('financialDocumentType')}
                              onChange={e => {
                                setFieldValue('financialDocumentType', e.currentTarget.value);
                              }}>
                              <option disabled key="-1" value="">
                                Select a type
                              </option>
                              {Object.keys(Bixby.Enums.FinancialDocumentType).map((value, ix) => {
                                return (
                                  <option key={ix} value={value}>
                                    {value}
                                  </option>
                                );
                              })}
                            </select>
                          </>
                        );
                      }}
                    />
                  </InputBlock>
                  <ErrorMessage name="financialDocumentType">
                    {msg => <div className="error error-message">{msg}</div>}
                  </ErrorMessage>
                </>
                <>
                  <InputBlock title="Date">
                    <Field
                      name="financialDocumentDate"
                      render={() => {
                        return (
                            <MonthDatePickerField
                              name="financialDocumentDate"
                              value={values.financialDocumentDate}
                              dateFormat="MMM-yy"
                              isClearable
                              onChange={(field: string, value: Date | null) => {
                                if (!value) {
                                  setFieldValue(field, undefined);
                                  return;
                                }
                                const endofmonth = dayjs(value).endOf('month').startOf('day').toDate();
                                setFieldValue(field, endofmonth);
                              }} />
                        );
                      }}
                    />
                  </InputBlock>
                  <ErrorMessage name="financialDocumentDate">
                    {msg => <div className="error error-message">{msg}</div>}
                  </ErrorMessage>
                </>
              </>
            )}
            {values.companyDocumentCategory_Id && values.companyDocumentCategory_Id === ORG_CHART_CATEGORY && (
              <>
                <InputBlock title="Page Number">
                  <Field
                    type="text"
                    name="orgChartPageNumber"
                    render={() => {
                      return (
                        <input
                          name="orgChartPageNumber"
                          type="number"
                          placeholder="Enter Page Number"
                          value={values.orgChartPageNumber}
                          onChange={e => {
                            setFieldValue('orgChartPageNumber', e.target.value);
                          }}
                        />
                      );
                    }}
                  />
                </InputBlock>
                <ErrorMessage name="orgChartPageNumber">
                  {msg => <div className="error error-message">{msg}</div>}
                </ErrorMessage>
              </>
            )}
            <div className="button_container">
              <BixButton
                type="button"
                onClick={handleSaveCategory}
                disabled={!values.companyDocumentCategory_Id || Object.keys(errors).length > 0}>
                Ok
              </BixButton>
              <BixButton light type="button" onClick={() => onCancel(id)}>
                Cancel
              </BixButton>
            </div>
          </>
        )}></Formik>
    </div>
  );
};

export default CompanyDocumentsCategoryPicker;
